/**
 * Broker recipient roster resolution (extracted 2026-07-15 for P1 fixtures).
 *
 * Resolves the login-bearing broker user(s) for a selected broker document
 * from the tenant roster already loaded by AppLayout. Exact brokerDocId
 * linkage is authoritative; a UNIQUE normalised commission/display-name match
 * covers legacy/canonical broker-document drift. Ambiguous name matches fail
 * closed (empty result) rather than guessing, and a browser-supplied UID is
 * never trusted.
 */

export interface RosterMember {
  id: string;
  role?: string;
  companyId?: string;
  brokerDocId?: string;
  displayName?: string;
  email?: string;
  commissionDataName?: string;
  [key: string]: unknown;
}

export function normaliseRosterName(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLocaleLowerCase('en-AU').replace(/\s+/g, ' ')
    : '';
}

export function resolveBrokerRecipientUsers(input: {
  companyId: string;
  selectedBrokerDocId: string;
  selectedBrokerName?: string | null;
  teamMembers: RosterMember[];
}): RosterMember[] {
  const selectedName = normaliseRosterName(input.selectedBrokerName);
  const companyRoster = input.teamMembers.filter(
    (member) => !member.companyId || member.companyId === input.companyId,
  );
  const brokerUsers = companyRoster.filter((member) => member.role === 'broker');

  const exactBrokerUsers = brokerUsers.filter(
    (member) => member.brokerDocId === input.selectedBrokerDocId,
  );
  if (exactBrokerUsers.length > 0) return exactBrokerUsers;

  if (!selectedName) return [];
  const nameBrokerUsers = brokerUsers.filter((member) => {
    const commissionName = normaliseRosterName(member.commissionDataName);
    const displayName = normaliseRosterName(member.displayName);
    return commissionName === selectedName || displayName === selectedName;
  });
  // Fail closed on ambiguity: two candidate logins for one broker document
  // means we cannot know whose gross visibility the admin is editing.
  return nameBrokerUsers.length === 1 ? nameBrokerUsers : [];
}
